import {
    AuthContext,
    BaseButton,
    BaseNavLink,
    createRequestFilters,
    DataTable,
    DataTableCell,
    DataTablePresenter,
    IAuthContextProps,
    IFilterSchema,
    ITableColumn,
    ITableFetchOptions,
    ListProviderClearinghouseEraItemsRequest,
    ProviderClearinghouseEraItem,
    renderAmount,
    renderExpand,
} from '@enter/core';
import { mdiMenuRight } from '@mdi/js';
import { Icon } from '@mdi/react';
import { DataTableCard, PageHeader, PageLayout, PageLayoutRow } from 'components';
import { inject, observer } from 'mobx-react';
import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { Row } from 'react-table';
import { DialogContext, IDialogContextProps } from 'services';
import { IInjectedRootStore, IObservedRootStore, parseItemListingRequest } from 'stores';
import { CLAIM_STATUS_LABELS, ClearinghouseStore, EndpointUnavailableError } from 'stores/clearinghouse.store';
import { ClearinghouseUnavailableNotice } from './clearinghouse-endpoint-notice';
import * as S from './clearinghouses.styles';

interface IClearinghouseEraItemsProps extends IInjectedRootStore {
    authContext: IAuthContextProps;
    dialogContext: IDialogContextProps;
    accountId?: string;
    clearinghouseId?: string;
    eraId?: string;
}

interface IClearinghouseEraItemsState {
    loading: boolean;
    clearinghouseName?: string;
    payerName?: string;
    /** Set when the items endpoint answered with an error; the page names it instead of showing "no claims". */
    unavailable?: EndpointUnavailableError;
}

const FETCH_GROUP = 'clearinghouse-era-items';

const fullName = (first?: string, last?: string): string | undefined =>
    first || last ? [first, last].filter(Boolean).join(' ') : undefined;

/**
 * The support console's item columns, read-only: no Tags (a support entity) and no Payments column,
 * whose job there is the "Create payment" action — a write a provider does not get here.
 */
const columns: ITableColumn<ProviderClearinghouseEraItem>[] = [
    {
        id: 'id',
        Header: 'Id',
        accessor: 'id',
        Cell: DataTableCell('String'),
        disableSortBy: true,
        show: false,
    },
    {
        id: 'npi',
        Header: 'Provider NPI',
        accessor: 'npi',
        Cell: DataTableCell('String'),
        disableSortBy: true,
        show: false,
    },
    {
        id: 'pcn',
        Header: 'Patient control number',
        accessor: 'pcn',
        Cell: ({ cell }) => <S.IdCell>{cell.value}</S.IdCell>,
        disableSortBy: true,
    },
    {
        id: 'icn',
        Header: 'Payer claim number',
        accessor: 'icn',
        Cell: ({ cell }) => <S.IdCell>{cell.value}</S.IdCell>,
        disableSortBy: true,
    },
    {
        id: 'crossoverCarrier',
        Header: 'Crossover carrier',
        accessor: 'crossoverCarrier',
        Cell: DataTableCell('String'),
        disableSortBy: true,
        show: false,
    },
    {
        id: 'subscriberId',
        Header: 'Subscriber ID',
        accessor: 'subscriberId',
        Cell: ({ cell }) => <S.IdCell>{cell.value}</S.IdCell>,
        disableSortBy: true,
    },
    {
        id: 'subscriber',
        Header: 'Subscriber',
        accessor: row => fullName(row.subscriberFirst, row.subscriberLast),
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'patient',
        Header: 'Patient',
        accessor: row => fullName(row.patientFirst, row.patientLast),
        Cell: DataTableCell('String'),
        disableSortBy: true,
    },
    {
        id: 'chargeAmount',
        Header: 'Charge amount',
        accessor: 'chargeAmount',
        Cell: DataTableCell('Amount'),
        disableSortBy: true,
    },
    {
        id: 'paidAmount',
        Header: 'Paid amount',
        accessor: 'paidAmount',
        Cell: DataTableCell('Amount'),
        disableSortBy: true,
    },
    {
        id: 'fromDos',
        Header: 'Date of service',
        accessor: 'fromDos',
        Cell: DataTableCell('Date'),
        disableSortBy: true,
    },
    {
        id: 'throughDos',
        Header: 'To date of service',
        accessor: 'throughDos',
        Cell: DataTableCell('Date'),
        disableSortBy: true,
        show: false,
    },
    {
        id: 'statusCode',
        Header: 'Status',
        accessor: 'statusCode',
        // CLP02 is a bare number on the wire; a biller reads "Denied", not "4".
        Cell: ({ cell }) => (
            <S.Cell>{cell.value ? `${cell.value} · ${CLAIM_STATUS_LABELS[cell.value] || 'Other'}` : '-'}</S.Cell>
        ),
        disableSortBy: true,
    },
    {
        id: 'created',
        Header: 'Created',
        accessor: 'created',
        Cell: DataTableCell('DateTime'),
        disableSortBy: true,
        show: false,
    },
    {
        id: 'charges',
        Header: 'Charges',
        accessor: row => `${(row.charges || []).length} line${(row.charges || []).length === 1 ? '' : 's'}`,
        Cell: props => renderExpand(props)(props.value),
        disableSortBy: true,
    },
];

const filterSchemas: IFilterSchema<keyof ListProviderClearinghouseEraItemsRequest>[] = [
    { label: 'Patient control number', filterId: 'filterPcn', columnId: 'pcn', filterType: 'String' },
    { label: 'Payer claim number', filterId: 'filterIcn', columnId: 'icn', filterType: 'String' },
    { label: 'Patient last name', filterId: 'filterPatientLastName', columnId: 'patient', filterType: 'String' },
    {
        label: 'Subscriber last name',
        filterId: 'filterSubscriberLastName',
        columnId: 'subscriber',
        filterType: 'String',
    },
];

/** The charge lines of one claim — the 835 SVC loops — with their CAS adjustments spelled out. */
function renderCharges(row: Row<ProviderClearinghouseEraItem>): React.ReactElement {
    const charges = row.original.charges || [];

    return (
        <S.Charges>
            <S.ChargesTable>
                <thead>
                    <tr>
                        <S.ChargesTH>Line</S.ChargesTH>
                        <S.ChargesTH>Procedure</S.ChargesTH>
                        <S.ChargesTH>Units</S.ChargesTH>
                        <S.ChargesTH>Date of service</S.ChargesTH>
                        <S.ChargesTH>Charge</S.ChargesTH>
                        <S.ChargesTH>Paid</S.ChargesTH>
                        <S.ChargesTH>Adjustments</S.ChargesTH>
                    </tr>
                </thead>
                <tbody>
                    {charges.map((charge, index) => (
                        <tr key={`${charge.controlNumber}-${index}`}>
                            <S.ChargesTD>{charge.controlNumber || index + 1}</S.ChargesTD>
                            <S.ChargesTD>
                                {[charge.procedureCode, ...(charge.modifiers || [])].filter(Boolean).join('-')}
                            </S.ChargesTD>
                            <S.ChargesTD>{charge.units ?? '-'}</S.ChargesTD>
                            <S.ChargesTD>{charge.fromDos || '-'}</S.ChargesTD>
                            <S.ChargesMoney>{renderAmount(charge.chargeAmount)}</S.ChargesMoney>
                            <S.ChargesMoney>{renderAmount(charge.paidAmount)}</S.ChargesMoney>
                            <S.ChargesTD>
                                {(charge.adjustments || []).length
                                    ? (charge.adjustments || [])
                                          .map(adj => `${adj.group}-${adj.code} ${renderAmount(adj.amount)}`)
                                          .join(' · ')
                                    : '-'}
                            </S.ChargesTD>
                        </tr>
                    ))}
                </tbody>
            </S.ChargesTable>
        </S.Charges>
    );
}

/** The table's empty state while the endpoint is unavailable — "not loaded", never "none" (NS-3). */
const NotLoaded: React.FC = () => <S.Cell>Not loaded — see the notice above.</S.Cell>;

class ClearinghouseEraItemsPage extends React.Component<IClearinghouseEraItemsProps, IClearinghouseEraItemsState> {
    private presenter: DataTablePresenter<ProviderClearinghouseEraItem>;

    constructor(props: IClearinghouseEraItemsProps) {
        super(props);
        this.state = { loading: false };
        this.presenter = new DataTablePresenter<ProviderClearinghouseEraItem>({
            name: FETCH_GROUP,
            data: [],
            columns,
            filterSchemas,
        });
    }

    public componentDidMount(): void {
        document.title = 'ENTER Platform - Clearinghouse era items';
        this.loadBreadcrumbLabels();
    }

    public componentWillUnmount(): void {
        this.props.authContext.cancelGroup(FETCH_GROUP);
    }

    public render(): React.ReactElement {
        const { accountId, clearinghouseId } = this.props;
        const { clearinghouseName, payerName, unavailable } = this.state;
        const root = accountId ? `/${accountId}/clearinghouses` : '/clearinghouses';

        return (
            <PageLayout>
                <PageLayoutRow>
                    <S.Breadcrumbs>
                        <BaseNavLink to={root}>
                            {(): React.ReactElement => <BaseButton variant="text">Clearinghouses</BaseButton>}
                        </BaseNavLink>
                        <Icon path={mdiMenuRight} size={0.75} />
                        <BaseNavLink to={`${root}/${clearinghouseId}`}>
                            {(): React.ReactElement => (
                                <BaseButton variant="text">{clearinghouseName || 'Clearinghouse'}</BaseButton>
                            )}
                        </BaseNavLink>
                        <Icon path={mdiMenuRight} size={0.75} />
                        {!!payerName && <BaseButton variant="text">{payerName}</BaseButton>}
                    </S.Breadcrumbs>
                </PageLayoutRow>
                <PageHeader title="Clearinghouse era items" hideDateRange={true} />
                {unavailable && (
                    <PageLayoutRow>
                        <ClearinghouseUnavailableNotice error={unavailable} what="This ERA's claims" />
                    </PageLayoutRow>
                )}
                <PageLayoutRow expand={true}>
                    <DataTableCard size="large">
                        <DataTable
                            presenter={this.presenter}
                            isLoading={this.state.loading}
                            onFetch={this.onFetch}
                            renderRowSubComponent={renderCharges}
                            NotFoundComponent={unavailable ? NotLoaded : undefined}
                        />
                    </DataTableCard>
                </PageLayoutRow>
            </PageLayout>
        );
    }

    /** Demo mode must never render real account data (NS-14). */
    private get isDemo(): boolean {
        return !!this.props.rootStore?.isDemo();
    }

    /**
     * Decoration for the breadcrumb — failures must not affect the list below. Both are real reads:
     * the clearinghouse by id, and the ERA by id (`GET /provider/clearinghouse/era/{id}`).
     */
    private loadBreadcrumbLabels = async (): Promise<void> => {
        const { clearinghouseId, eraId } = this.props;
        if (!clearinghouseId || !eraId || this.isDemo) {
            return;
        }
        try {
            const clearinghouse = await ClearinghouseStore.get(clearinghouseId);
            this.setState({ clearinghouseName: clearinghouse.name });
        } catch {
            // The id in the URL is the fact; the name is only a label.
        }
        try {
            const era = await ClearinghouseStore.getEra(eraId);
            this.setState({ payerName: era.payerName });
        } catch {
            // Same: a label, not data.
        }
    };

    private onFetch = async (fetchOptions: ITableFetchOptions): Promise<void> => {
        const { clearinghouseId, eraId } = this.props;
        if (this.isDemo || !clearinghouseId || !eraId) {
            this.presenter.onDataUpdate([]);
            this.presenter.onDataTotalChange(0);
            return;
        }

        try {
            this.setState({ loading: true });
            this.props.authContext.cancelGroup(FETCH_GROUP);
            // The notice above reports a failed read; the global error dialog would repeat it on every page.
            this.props.authContext.setGroup(FETCH_GROUP, { skipErrorHandler: true });

            const { pageIndex, pageSize, validFilters } = fetchOptions;
            const { limit, offset } = parseItemListingRequest({ page: pageIndex, pageSize });

            const listing = await ClearinghouseStore.listEraItems(clearinghouseId, eraId, {
                ...createRequestFilters(validFilters, true),
                limit,
                offset,
            });

            this.presenter.onDataUpdate(listing.items);
            this.presenter.onDataTotalChange(listing.total || 0);
            this.setState({ loading: false, unavailable: undefined });
        } catch (err) {
            if (err instanceof EndpointUnavailableError) {
                this.presenter.onDataUpdate([]);
                this.presenter.onDataTotalChange(0);
                this.setState({ loading: false, unavailable: err });
                return;
            }
            this.setState({ loading: false });
            this.props.dialogContext.showError(err as Error);
        } finally {
            // `setGroup` moves a global cursor; put the error handler back for whatever fetches next.
            this.props.authContext.setGroup(FETCH_GROUP);
        }
    };
}

export const ClearinghouseEraItems: React.FC<IObservedRootStore> = inject('rootStore')(
    observer((props: IObservedRootStore) => {
        const { rootStore } = props;
        const { accountId, clearinghouseId, eraId } = useParams<{
            accountId: string;
            clearinghouseId: string;
            eraId: string;
        }>();
        const authContext = useContext(AuthContext);
        const dialogContext = useContext(DialogContext);
        if (!rootStore) {
            return <></>;
        }
        return (
            <ClearinghouseEraItemsPage
                accountId={accountId}
                clearinghouseId={clearinghouseId}
                eraId={eraId}
                authContext={authContext}
                dialogContext={dialogContext}
                rootStore={rootStore}
            />
        );
    }),
);
